import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";

const TermsOfService: React.FC = () => {
  const theme = useTheme();

  const lastUpdated = "December 3, 2025";
  const contactEmail = "merraiofficial@gmail.com";

  const sections: Array<{
    title: string;
    content?: string;
    subsections?: Array<{ subtitle: string; content: string }>;
  }> = [
    {
      title: "1. Agreement to Terms",
      content:
        "These Terms of Service (“Terms”) form a legally binding agreement between you (“you”, “your”) and MeraAI (“we”, “us”, “our”). By accessing or using the Services, you agree to these Terms. If you do not agree, do not use the Services.",
    },
    {
      title: "2. The Services",
      content:
        "MeraAI provides software and tools that may help businesses automate customer conversations and workflows, including optional integrations with third-party platforms like WhatsApp Business (Meta) and Shopify.\n\nThe Services may change over time (we may add, remove, or modify features).",
    },
    {
      title: "3. Eligibility",
      content:
        "You must be at least 18 years old (or the age of majority in your jurisdiction) and have the authority to enter into these Terms. If you use the Services on behalf of a business, you represent that you are authorized to bind that business to these Terms.",
    },
    {
      title: "4. Account Registration and Security",
      subsections: [
        {
          subtitle: "4.1 Creating an account",
          content:
            "You agree to provide accurate and complete information and keep it updated. We may suspend or terminate accounts that contain false or misleading information.",
        },
        {
          subtitle: "4.2 Keeping your account secure",
          content:
            "You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account. Notify us promptly if you suspect unauthorized access.",
        },
      ],
    },
    {
      title: "5. Plans, Billing, and Renewal",
      subsections: [
        {
          subtitle: "5.1 Plans and pricing",
          content:
            "If the Services include paid plans, pricing and plan details are shown on our website or within the product. We may update pricing or plan features from time to time. Any changes generally apply prospectively (not retroactively) to the next billing cycle where permitted by law.",
        },
        {
          subtitle: "5.2 Payments",
          content:
            "If you purchase a paid plan:\n• You authorize us (and our payment processors) to charge your selected payment method\n• Fees are billed in advance (monthly or annually, depending on your plan)\n• Taxes may apply where required\n• Unless required by law, fees are non-refundable",
        },
        {
          subtitle: "5.3 Auto-renewal and cancellation",
          content:
            "Subscriptions renew automatically unless you cancel before the renewal date. You can cancel through your account settings (if available). Cancellation takes effect at the end of the current billing period.",
        },
      ],
    },
    {
      title: "6. Acceptable Use",
      content:
        "You agree not to:\n• Break any laws or regulations\n• Infringe intellectual property or privacy rights\n• Send spam, phishing, malware, or harmful content\n• Misrepresent your identity or impersonate others\n• Attempt unauthorized access, interfere with security, or disrupt the Services\n• Reverse engineer or attempt to extract source code (except to the extent permitted by law)\n• Scrape or collect data without permission\n• Use the Services to transmit third-party confidential data without permission\n\nWe may suspend or terminate access if we believe your use is unsafe, abusive, unlawful, or harms the Services or others.",
    },
    {
      title: "7. Third-Party Services (WhatsApp/Meta, Shopify, etc.)",
      content:
        "The Services may integrate with third-party platforms (e.g., WhatsApp Business (Meta) and Shopify). Your use of those platforms is governed by their own terms and policies. We are not responsible for third-party platforms, including their availability, changes, or outages.\n\nYou are responsible for obtaining any required consents and providing any required notices to your customers when using messaging or e-commerce features.",
    },
    {
      title: "8. Your Content and Data",
      subsections: [
        {
          subtitle: "8.1 Ownership",
          content:
            "You own or control the data and content you provide to the Services (“User Content”).",
        },
        {
          subtitle: "8.2 Permission to operate the Services",
          content:
            "You grant MeraAI a limited license to host, store, process, transmit, and display User Content solely to provide, maintain, and improve the Services (including troubleshooting, security, and performance).",
        },
        {
          subtitle: "8.3 Responsibility for User Content",
          content:
            "You are responsible for User Content and represent that you have all rights and permissions needed to provide it to us and to use it with the Services.",
        },
      ],
    },
    {
      title: "9. Intellectual Property",
      content:
        "The Services, software, and related materials are owned by MeraAI and/or its licensors and are protected by applicable intellectual property laws. We grant you a limited, non-exclusive, non-transferable license to use the Services for your internal business purposes during your subscription or permitted use.",
    },
    {
      title: "10. Feedback",
      content:
        "If you provide feedback, suggestions, or ideas, you grant us the right to use them without restriction or compensation to improve the Services.",
    },
    {
      title: "11. Disclaimer of Warranties",
      content:
        "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE”. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not guarantee that the Services will be uninterrupted, secure, or error-free.",
    },
    {
      title: "12. Limitation of Liability",
      content:
        "TO THE MAXIMUM EXTENT PERMITTED BY LAW:\n• We will not be liable for indirect, incidental, special, consequential, or punitive damages\n• We are not liable for third-party platform failures (including WhatsApp/Meta or Shopify)\n• Our total liability for any claim will not exceed the amount you paid us for the Services in the 12 months before the event giving rise to the claim (or, if you have not paid, a limited amount as required by law)\n\nSome jurisdictions do not allow certain limitations, so some of the above may not apply to you.",
    },
    {
      title: "13. Indemnification",
      content:
        "You agree to defend, indemnify, and hold harmless MeraAI from and against claims, damages, losses, and expenses (including reasonable legal fees) arising out of or related to: (a) your use of the Services, (b) your User Content, (c) your violation of these Terms, or (d) your violation of applicable laws or third-party rights.",
    },
    {
      title: "14. Suspension and Termination",
      subsections: [
        {
          subtitle: "14.1 Termination by you",
          content:
            "You may stop using the Services at any time and may cancel your subscription through your account settings (if available).",
        },
        {
          subtitle: "14.2 Suspension or termination by us",
          content:
            "We may suspend or terminate your access to the Services if we reasonably believe you violated these Terms, used the Services unlawfully, created a security risk, or caused harm to the Services or others. Where practical, we’ll provide notice.",
        },
      ],
    },
    {
      title: "15. Data Retention and Deletion",
      content:
        "Our data handling and retention practices are described in our Privacy Policy. You are responsible for exporting or backing up your data if needed. Certain data may be retained as required for security, compliance, or legal purposes.",
    },
    {
      title: "16. Dispute Resolution and Governing Law",
      content:
        "Disputes should be raised with us first so we can try to resolve them informally. If we cannot resolve a dispute, it will be handled under applicable law and procedures based on the jurisdiction relevant to your account and use of the Services.\n\nNothing in these Terms limits any rights you may have under applicable consumer protection laws.",
    },
    {
      title: "17. Changes to These Terms",
      content:
        "We may update these Terms from time to time. Updated Terms will be posted on our website and may be communicated through the Services. Continued use of the Services after the effective date of an update means you accept the updated Terms.",
    },
    {
      title: "18. Contact",
      content:
        "For questions about these Terms or the Services, contact us at:\n\nEmail: " +
        contactEmail,
    },
  ];

  return (
    <Box>
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
          {/* Header */}
          <Stack spacing={2} sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Terms of Service
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Please read these Terms carefully. By using MeraAI, you agree to
              them.
            </Typography>
          </Stack>

          <Divider sx={{ mb: 4 }} />

          {/* Content Sections */}
          <Stack spacing={4}>
            {sections.map((section, index) => (
              <Box key={index}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ mb: 2, color: theme.palette.primary.main }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.8,
                    }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.subsections && (
                  <Stack spacing={2}>
                    {section.subsections.map((subsection, subIndex) => (
                      <Box key={subIndex}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1, color: theme.palette.text.primary }}
                        >
                          {subsection.subtitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.8,
                          }}
                        >
                          {subsection.content}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 4 }} />

          {/* Footer Note */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Questions about these Terms? Email us at {contactEmail}.
            </Typography>
          </Box>
      </Container>
    </Box>
  );
};

export default TermsOfService;
